import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { datasetService, fieldService, referenceService, relationshipService } from '../services';
import {
  AttributeSensitivity,
  DatasetSubtypeOperation,
  formatDatasetSubtitle,
  formatData,
  handleError,
  Role,
  Asset,
  Operation,
  updateFieldsWithRelationships,
  getHierarchyPath,
  getObjectNameFromSelection,
  fetchDatasetFields,
  searchDatasetFields,
  withFieldPaths,
  IsNullableValues,
  sourceLinkAction,
  technicalDefinitionLanguage,
} from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  Pill,
  References,
  LineageGraph,
  RelationshipGraph,
  Tabs,
  fetchToReferencesCount,
  TechnicalDefinition,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

export const Dataset = ({ abortFetch }) => {
  const { queryParams } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [dataset, setDataset] = useState([]);
  const [fields, setFields] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [toReferencesCount, setToReferencesCount] = useState({});
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentDatasetAncestors] = useGlobalState('currentDatasetAncestors');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);

  // Child datasets shown in the "Datasets" tab (only for dataset types that can have children).
  // Kept separate from the Fields-tab state so selections in the two tabs do not collide.
  const [childDatasets, setChildDatasets] = useState();
  // The fields of the child datasets that have been opened, held apart from the datasets so that
  // reloading them — which every save does — does not empty the open rows.
  const [childDatasetFields, setChildDatasetFields] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [selectedChildItems, setSelectedChildItems] = useState([]);

  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  /** Clears the Fields-tab row selection. */
  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  /** Clears the Datasets-tab (child datasets) row selection. */
  const clearSelectedChildItems = () => {
    setSelectedChildItems([]);
  };

  const { system_id, datasource_id, dataset_group_id, dataset_id } = useParams();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Dataset,
    objectIds: [dataset_id],
  });
  const { appendStatus } = useStatus({ setState: setFields, assetCategory: Asset.System });
  // Separate status appender bound to the child-datasets state for the "Datasets" tab table.
  const { appendStatus: appendChildStatus } = useStatus({
    setState: setChildDatasets,
    assetCategory: Asset.System,
  });

  useEffect(() => {
    // Necessary if one navigates from one dataset to another
    setDefaultSelectedTab({ tab: queryParams.tab });

    fetchItems();
    fetchFields();

    fetchToReferencesCount({ toObjectName: Asset.Dataset, toObjectId: dataset_id, abortFetch })
      .then(res => setToReferencesCount(res))
      .catch(err => {
        handleError({ err, showMessage });
      });
  }, [dataset_id]);

  const fetchItems = async () => {
    datasetService
      .getById(dataset_id, abortFetch)
      .then(res => {
        if (res.datasets.length < 1) {
          setNotFound(true);
        } else {
          const hasChildren = res.datasets[0].dataset_type_has_children;
          setDataset(res.datasets[0]);
          fetchFromReferences(dataset_id);

          // Now that the dataset's type is known, select the right primary tab: "Datasets" for
          // parents (the Fields tab is hidden) and "Fields" otherwise. An explicit ?tab= in the
          // URL wins, except a stale value pointing at the hidden one of the datasets/fields pair
          // (e.g. carried over when navigating between a parent and a leaf dataset).
          const primaryTab = hasChildren ? 'datasets' : 'fields';
          const hiddenPrimaryTab = hasChildren ? 'fields' : 'datasets';
          setDefaultSelectedTab({
            tab:
              queryParams.tab && queryParams.tab !== hiddenPrimaryTab
                ? queryParams.tab
                : primaryTab,
          });

          // Parents hold nested child datasets rather than fields — load them for the tab.
          if (hasChildren) {
            fetchChildDatasets();
          }
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(() => setLoading(false));
  };

  const fetchFields = async () => {
    const promises = [
      fieldService.getByDatasetId(dataset_id, abortFetch).catch(err => {
        throw err;
      }),
      relationshipService.getByObjectId(Asset.Dataset, dataset_id, abortFetch).catch(err => {
        throw err;
      }),
    ];

    Promise.all(promises)
      .then(result => {
        const fields = result[0].fields.map(x => ({
          ...x,
          pii: x.is_pii ? 'PII' : null,
          pii_tooltip: x.is_pii ? 'Classified as Personally Identifiable Information (PII)' : null,
          connector_filter_name:
            x.connector_name && x.secondary_connector_name
              ? 'both'
              : x.connector_name || x.secondary_connector_name || 'none',
          field_path: getHierarchyPath(x, result[0].fields),
        }));

        //setFields(fields);
        const newFields = updateFieldsWithRelationships({
          fields,
          relationships: result[1].relationships,
        });
        setFields(newFields);
        appendStatus();
        setLoadingTable(false);
        fetchPrincipals(newFields, setFields);
      })
      .catch(err => {
        handleError({ err, showMessage });
      })
      .finally(() => {
        setLoadingTable(false);
      });
  };

  const fetchFromReferences = async datasetId => {
    referenceService
      .getByFromId(Asset.Dataset, datasetId || dataset_id, abortFetch)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Returns the rows that form the subtree rooted at the current dataset (all descendants,
   * excluding the dataset itself), from a flat list of datasets keyed by parent_dataset_id.
   * The current dataset's direct children are re-rooted (parent_dataset_id set to null) so the
   * DataTable's hierarchy hook treats them as top-level rows — their real parent (the current
   * dataset) is intentionally not in this list. Deeper descendants keep their parent_dataset_id
   * and nest under those rows via the DataTable's parentIdColumn.
   * @param {Array<{ dataset_id: number, parent_dataset_id: number|null }>} rows
   * @returns {Array<object>}
   */
  const getDescendantDatasets = rows => {
    const rootId = parseInt(dataset_id);

    // Group rows by their parent id so the subtree can be walked without repeated scans.
    const childrenByParent = new Map();
    rows.forEach(row => {
      const key = row.parent_dataset_id;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key).push(row);
    });

    // Walk the tree to collect the ids in the subtree (order here is irrelevant — it is a set).
    const descendantIds = new Set();
    const stack = [...(childrenByParent.get(rootId) || [])];
    while (stack.length) {
      const node = stack.pop();
      descendantIds.add(node.dataset_id);
      const children = childrenByParent.get(node.dataset_id);
      if (children) stack.push(...children);
    }

    // Build the result from the original rows so the backend's sort order (ascending by name) is
    // preserved, re-rooting the current dataset's direct children so they show as top-level rows.
    return rows
      .filter(row => descendantIds.has(row.dataset_id))
      .map(row =>
        row.parent_dataset_id === rootId
          ? { ...row, parent_dataset_id: null, parent_unique_id: null }
          : row
      );
  };

  /**
   * Fetches the datasets of the current dataset's group, decorates them like the DatasetGroup
   * table, and keeps only the subtree below the current dataset for the "Datasets" tab. Combines
   * with field statistics so the documentation/field-count bars render.
   */
  const fetchChildDatasets = async () => {
    const promise = new Promise(resolve => {
      datasetService
        .getByDatasetGroupId(dataset_group_id, abortFetch)
        .then(res => {
          const allDatasets = res?.datasets || [];
          const decorated = allDatasets.map(x => ({
            ...x,
            // The tab holds datasets and fields together, so a row is identified by its kind and
            // its id rather than by an id that only means something within one table.
            unique_id: `${Asset.Dataset}-${x.dataset_id}`,
            parent_unique_id: x.parent_dataset_id
              ? `${Asset.Dataset}-${x.parent_dataset_id}`
              : null,
            object_id: x.dataset_id,
            object_name: Asset.Dataset,
            link: `/systems/${x.system_id}/${x.datasource_id}/${x.dataset_group_id}/${x.dataset_id}`,
            className: 'bold',
            // The type icon shown in the name cell outside API datasources. A property of its own so
            // it marks datasets only — a field carries its dataset's type as well.
            dataset_type_icon: x.dataset_type_name,
            pii: x.is_pii ? 'PII' : null,
            pii_tooltip: x.is_pii
              ? 'Contains Fields classified as Personally Identifiable Information (PII)'
              : null,
            connector_filter_name:
              x.connector_name && x.secondary_connector_name
                ? 'both'
                : x.connector_name || x.secondary_connector_name || 'none',
            // API method pill is shown inline before the name for top-level datasets only.
            dataset_subtype_operation_top:
              x.parent_dataset_id == null ? x.dataset_subtype_operation : undefined,
            dataset_subtype_tooltip: x?.dataset_subtype_code?.toUpperCase() + ' HTTP method',
            // Child datasets show their dataset_type icon inline; top-level shows the pill instead.
            dataset_type_child: x.parent_dataset_id == null ? undefined : x.dataset_type_name,
            dataset_path: getHierarchyPath(x, allDatasets, Asset.Dataset, ' > '),
          }));

          const datasets = getDescendantDatasets(decorated);

          setChildDatasets(datasets);
          appendChildStatus();
          setLoadingChildren(false);
          fetchPrincipals(datasets, setChildDatasets);
          resolve(datasets);
        })
        .catch(err => {
          handleError({ err, showMessage }, () => {
            setLoadingChildren(false);
          });
        });
    });

    Promise.all([promise, fetchChildFieldStatistics()])
      .then(result => {
        updateChildFieldStatistics(result[0], result[1]);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Fetches field statistics for the current dataset's group. The result covers the whole group;
   * updateChildFieldStatistics matches them to the visible child rows by dataset_id.
   * @returns {Promise<Array<object>>}
   */
  const fetchChildFieldStatistics = async () => {
    return new Promise(resolve => {
      datasetService
        .getFieldStatistics(dataset_group_id, abortFetch)
        .then(res => {
          resolve(res.field_statistics);
        })
        .catch(() => {
          return null;
        });
    });
  };

  /**
   * Merges field statistics into the child datasets state and computes the per-column maxima used
   * by the chart columns (field count, row count, disk size).
   * @param {Array<object>} data - the child dataset rows currently held in state
   * @param {Array<object>} statistics - field statistics for the group, keyed by dataset_id
   */
  const updateChildFieldStatistics = (data, statistics) => {
    let statistic = {};
    let maxFieldCount = 0;
    let maxRowCount = 0;
    let maxDiskSize = 0;

    statistics.forEach(row => {
      if (row.field_count > maxFieldCount) {
        maxFieldCount = row.field_count;
      }
    });

    data.forEach(row => {
      if (parseInt(row.dataset_row_count) > maxRowCount) {
        maxRowCount = parseInt(row.dataset_row_count);
      }
      if (parseInt(row.dataset_size_bytes) > maxDiskSize) {
        maxDiskSize = parseInt(row.dataset_size_bytes);
      }
    });

    setChildDatasets(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.dataset_id === item.dataset_id);
        // Statistics are rolled up over the dataset subtree on the backend, so a parent dataset's
        // field count, documentation level, sensitivity and PII reflect all its nested children.
        // Override the row's own-attribute sensitivity/PII (from v_dataset) with the rolled-up values.
        const isPii = statistic ? statistic.is_pii : item.is_pii;
        return {
          ...item,
          field_count: statistic ? statistic.field_count : 0,
          attribute_ratio: statistic ? statistic.attribute_ratio : 0,
          field_count_max: maxFieldCount,
          // How many field rows appear directly under this dataset when it is opened, as opposed to
          // the rolled-up field_count above.
          own_field_count: statistic ? statistic.own_field_count : 0,
          attribute_sensitivity_level: statistic
            ? statistic.attribute_sensitivity_level
            : item.attribute_sensitivity_level,
          attribute_sensitivity_name: statistic
            ? statistic.attribute_sensitivity_name
            : item.attribute_sensitivity_name,
          is_pii: isPii,
          pii: isPii ? 'PII' : null,
          pii_tooltip: isPii
            ? 'Contains Fields classified as Personally Identifiable Information (PII)'
            : null,
          row_count: item.dataset_row_count ? parseInt(item.dataset_row_count) : null,
          row_count_max: maxRowCount,
          disk_size: item.dataset_size_bytes ? parseInt(item.dataset_size_bytes) : null,
          disk_size_max: maxDiskSize,
        };
      })
    );
  };

  const addField = async data => {
    const fields = { fields: Array.isArray(data) ? data : [data] };

    await fieldService
      .create(fields)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchFields();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Replaces the loaded fields of the given child datasets, leaving every other dataset's alone.
   * @param {Array<number|string>|number|string} datasetIds - The datasets just read
   * @param {object[]} rows - Their fields as just read, in one list
   */
  const mergeChildDatasetFields = (datasetIds, rows) => {
    // A set of what is being replaced, so re-reading many datasets is one pass over the state and
    // one render rather than one of each per dataset.
    const ids = new Set([].concat(datasetIds).map(Number));

    setChildDatasetFields(prev => [
      ...prev.filter(field => !ids.has(Number(field.dataset_id))),
      ...rows,
    ]);
    appendChildStatus({ setState: setChildDatasetFields, assetCategory: Asset.System });
  };

  /**
   * Loads the fields of one child dataset, for expanding its row. The promise is returned so the
   * table can show the row as loading until they arrive.
   *
   * A field's own children come with them, so opening a nested field costs no further request.
   *
   * @param {object} item - The row being expanded
   * @returns {Promise<void>|undefined} undefined when there is nothing to load for this row
   */
  const fetchChildDatasetFields = item => {
    if (item.object_name !== Asset.Dataset) return;

    // Already read once. The rows are still here — collapsing a row does not discard them — so
    // re-expanding shows them again without another request. Anything changed from this page re-reads
    // them, which is what keeps them current.
    if (childDatasetFields.some(field => Number(field.dataset_id) === Number(item.dataset_id))) {
      return;
    }

    return fetchDatasetFields({ datasetIds: [item.dataset_id], signal: abortFetch })
      .then(rows => mergeChildDatasetFields(item.dataset_id, withFieldPaths(rows, childDatasets)))
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Re-reads the fields of the child datasets whose rows are open, after one of them has changed.
   *
   * All of them are read in one request: opening a row keeps its fields until the page is left, so
   * what this covers grows as the tree is explored, and a request each would be a request per row
   * ever opened.
   *
   * @param {Array<number|string>} [datasetIds] - Datasets to re-read; defaults to every open one
   */
  const refreshChildDatasetFields = datasetIds => {
    const ids = datasetIds || [...new Set(childDatasetFields.map(field => field.dataset_id))];

    if (ids.length === 0) return Promise.resolve();

    return fetchDatasetFields({ datasetIds: ids })
      .then(rows => mergeChildDatasetFields(ids, withFieldPaths(rows, childDatasets)))
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Searches the fields of this dataset's subtree server-side, for the table's filter box.
   *
   * Scoped by the datasets on screen rather than by the whole group: a match under a dataset
   * elsewhere in the group would have no row to appear under.
   *
   * @param {string} term - What the user typed
   * @param {{signal: AbortSignal, top: number, fields: string[]}} options - From the DataTable
   * @returns {Promise<object[]>} Matching field rows
   */
  const searchChildDatasetFields = (term, { signal, top, fields: ticked }) => {
    const datasetIds = (childDatasets || [])
      .filter(row => row.object_name === Asset.Dataset)
      .map(row => row.dataset_id);

    if (datasetIds.length === 0) return Promise.resolve([]);

    return searchDatasetFields({
      scope: `dataset_id in (${datasetIds.join(',')})`,
      term,
      fields: ticked,
      top,
      signal,
    }).catch(err => {
      handleError({ err, showMessage });
      return [];
    });
  };

  /**
   * Opens the right dialog for the rows selected in the "Datasets" tab, dispatching on what kind of
   * asset they are: a dataset and a field are different resources with different forms.
   * @param {'add'|'add_child'|'add_field'|'edit'|'delete'} method
   * @param {object[]} [items] - Rows to act on; defaults to the tab's selection
   */
  const handleChildAsset = (method, items) => {
    items = items || selectedChildItems;
    const objectName = getObjectNameFromSelection(items, Asset.Dataset);

    if (objectName === Asset.Field) {
      switch (method) {
        case 'add':
          // The selection is a field, so a new one is added to the same dataset. Only the dataset is
          // carried over — the form must not copy the selected field's own properties.
          showSideDialog(
            'addField',
            [{ dataset_id: items[0].dataset_id, status_id: statuses.default_system_status_id }],
            addChildDatasetField
          );
          break;
        case 'add_child':
          showSideDialog('addChildField', items, addChildDatasetField);
          break;
        case 'edit':
          showSideDialog('editField', items, editChildDatasetField);
          break;
        case 'delete':
          showModalDialog('deleteField', items, deleteChildDatasetField);
          break;
        default:
        // nothing
      }
      return;
    }

    if (objectName === 'mixed') return;

    switch (method) {
      case 'add':
        // New datasets are parented under the current dataset — it is this tab's root.
        showSideDialog('addChildDataset', [data], addDataset);
        break;
      case 'add_child':
        showSideDialog('addChildDataset', items, addDataset);
        break;
      case 'add_field':
        // Adding a field to the selected dataset: the dataset is the parent, not something to copy.
        showSideDialog(
          'addField',
          [{ dataset_id: items[0].dataset_id, status_id: statuses.default_system_status_id }],
          addChildDatasetField
        );
        break;
      case 'edit':
        showSideDialog('editDataset', items, editDataset);
        break;
      case 'delete':
        showModalDialog('deleteDataset', items, deleteDataset);
        break;
      default:
      // nothing
    }
  };

  /**
   * Creates one or more fields under a child dataset and refreshes that dataset's rows.
   * @param {object|object[]} rows - The field(s) to create
   */
  const addChildDatasetField = async rows => {
    const fields = Array.isArray(rows) ? rows : [rows];

    await fieldService
      .create({ fields })
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        // The datasets carry the field counts, so they are reloaded alongside the fields themselves.
        fetchChildDatasets();
        refreshChildDatasetFields([...new Set(fields.map(field => field.dataset_id))]);
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Updates one or more fields shown in the "Datasets" tab.
   * @param {object|object[]} rows - The field(s) to update
   */
  const editChildDatasetField = async rows => {
    const fields = Array.isArray(rows) ? rows : [rows];

    await fieldService
      .update({ fields })
      .then(async res => {
        // An edit can move a field to another dataset, so both the dataset it left and the one it
        // arrived at are re-read — which is every open dataset, plus any newly named one.
        refreshChildDatasetFields([
          ...new Set([
            ...childDatasetFields.map(field => field.dataset_id),
            ...fields.map(field => field.dataset_id),
          ]),
        ]);
        fetchChildDatasets();
        showMessage(res.message);
        setSelectedChildItems([]);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Deletes one or more fields shown in the "Datasets" tab. The confirmation offers to delete their
   * child fields with them, so it answers with the rows and that choice.
   * @param {{items: object|object[], deleteChildren: boolean}} payload
   */
  const deleteChildDatasetField = async payload => {
    const items = Array.isArray(payload.items) ? payload.items : [payload.items];

    await fieldService
      .destroy({ fields: items, deleteChildren: payload.deleteChildren })
      .then(async res => {
        fetchChildDatasets();
        refreshChildDatasetFields();
        showMessage(res.message);
        hideModalDialog();
        setSelectedChildItems([]);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  /**
   * Creates one or more child datasets (from the "Datasets" tab) and refreshes the child tree.
   * @param {object|object[]} data - the dataset payload(s) from the add form
   */
  const addDataset = async data => {
    const datasets = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .create(datasets)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchChildDatasets();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Updates one or more datasets — either the current dataset (from its detail header) or a child
   * from the "Datasets" tab — and refreshes both the current dataset and the child tree.
   * @param {object|object[]} data - the dataset payload(s) from the edit form
   */
  const editDataset = async data => {
    const datasets = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(datasets)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        // Refresh the child tree only when this dataset can have children (the "Datasets" tab).
        if (dataset.dataset_type_has_children) {
          fetchChildDatasets();
          clearSelectedChildItems();
        }
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  const editField = async data => {
    const fields = { fields: Array.isArray(data) ? data : [data] };

    await fieldService
      .update(fields)
      .then(async res => {
        fetchFields();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog }, () => {
          throw err;
        });
      });
  };

  /**
   * Deletes one or more datasets. If the current dataset itself was deleted (from its detail
   * header) the view navigates away; if children were deleted from the "Datasets" tab, it stays
   * and refreshes the child tree.
   * @param {object[]} data - the dataset rows to delete
   */
  const deleteDataset = async data => {
    await datasetService
      .destroy({ datasets: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        // Was the current dataset among those deleted, or only children from the Datasets tab?
        const currentDatasetDeleted = data.some(d => d.dataset_id === parseInt(dataset_id));

        if (currentDatasetDeleted) {
          if (location.key !== 'default') {
            // Arrived here by navigating inside Katalogue, so return the user where they came from.
            navigate(-1);
          } else {
            // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
            // history to return to. Fall back to the deleted asset's parent.
            navigate(`/systems/${system_id}/${datasource_id}/${dataset_group_id}`);
          }
        } else {
          fetchChildDatasets();
          clearSelectedChildItems();
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const deleteField = async data => {
    const items = Array.isArray(data.items) ? data.items : [data.items];

    await fieldService
      .destroy({ fields: items, deleteChildren: data.deleteChildren })
      .then(async res => {
        fetchFields();
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const getAddChildButton = () => {
    if (selectedItems.length === 1 && selectedItems[0].datatype_has_children) {
      return {
        label: 'Add Child',
        tooltip: 'Add a new field with the currently selected field as parent',
        onClick: items => showSideDialog('addChildField', items || selectedItems, addField),
      };
    }
    return {};
  };

  const generateSubtitleIcon = data => {
    /*if (data.datasource_type_category === 'api' && data.parent_dataset_id) {
      const parentDataset = currentDatasetAncestors?.ancestors?.find(x => x.dataset_id === data.parent_dataset_id)
console.log(parentDataset)
      if (parentDataset && parentDataset.dataset_source_url) {
      return <Pill 
        label={parentDataset.dataset_subtype_name}
        className={DatasetSubtypeOperation[parentDataset?.dataset_subtype_operation]?.className}
      />
    }
    }*/

    return <Icon name={data.dataset_type_name} tooltip={data.dataset_type_name} />;
  };

  const data = loading ? [] : dataset;

  // Datasets whose type can have children (e.g. API endpoints) hold nested child datasets instead
  // of fields: show the "Datasets" tab and hide the "Fields" tab for them.
  const canHaveChildren = !!data.dataset_type_has_children;

  // The "Datasets" tab's rows: this dataset's descendants, plus the fields of the ones that have
  // been opened. The fields are rows of the same table, placed under their dataset — or under their
  // parent field — which keeps their checkbox and their columns in line with the rows above them.
  const childAssetRows = useMemo(
    () => [...(childDatasets || []), ...childDatasetFields],
    [childDatasets, childDatasetFields]
  );

  // Datasets and fields are separate resources with separate forms, so Edit and Delete act on one
  // kind at a time.
  const enableChildEdit =
    selectedChildItems.length === 0 ||
    selectedChildItems.every(item => item.object_name === selectedChildItems[0].object_name);

  const mixedChildSelectionText =
    'Assets of different types have been selected. Make sure to only select assets of the same type.';

  /**
   * Builds the "Add Child" button for the row selected in the "Datasets" tab: a child dataset under
   * a dataset that can have them, a field under a dataset that cannot, or a nested field under a
   * field whose datatype can hold one.
   * @returns {object} A DataTable button definition, or {} when the selection cannot have children
   */
  const getChildAddChildButton = () => {
    if (selectedChildItems.length !== 1) return {};

    const item = selectedChildItems[0];

    if (item.object_name === Asset.Field) {
      return {
        label: 'Add Child',
        disabled: !item.datatype_has_children,
        tooltip: item.datatype_has_children
          ? 'Add a new field with the currently selected field as parent'
          : 'Only a field whose datatype can hold other fields can have child fields',
        onClick: items => handleChildAsset('add_child', items),
      };
    }

    if (item.dataset_type_has_children) {
      return {
        label: 'Add Child',
        tooltip: 'Add a new dataset with the currently selected dataset as parent',
        onClick: items => handleChildAsset('add_child', items),
      };
    }

    // A dataset that cannot hold datasets can still hold fields, which is the only child it has.
    return {
      label: 'Add Child',
      tooltip: 'Add a new field to the currently selected dataset',
      onClick: items => handleChildAsset('add_field', items),
    };
  };

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }
  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Dataset"
          title={
            data.datasource_type_code === 'api' && data.dataset_subtype_id ? (
              <React.Fragment>
                <Pill
                  label={data.dataset_subtype_name}
                  className={DatasetSubtypeOperation[data.dataset_subtype_operation]?.className}
                />
                <span>{data.dataset_name}</span>
              </React.Fragment>
            ) : (
              data.dataset_name
            )
          }
          statusId={data.status_id}
          subtitleIcon={generateSubtitleIcon(data)}
          subtitleText={
            data &&
            !loading &&
            formatDatasetSubtitle(data, { ancestors: currentDatasetAncestors?.ancestors })
          }
          description={data.dataset_description}
          fromReferences={fromReferences}
          loading={loading}
          details={[
            {
              title: 'Rows',
              data: data.dataset_row_count
                ? formatData(parseInt(data.dataset_row_count), 'integer')
                : null,
            },
            {
              title: 'Disk Size',
              data: data.dataset_size_bytes
                ? formatData(parseInt(data.dataset_size_bytes), 'integer-disk-size')
                : null,
            },
            {
              title: (
                <React.Fragment>
                  <span>{`Source Description`}</span>
                  <span className="second-row">{`${data.secondary_connector_name}`}</span>
                </React.Fragment>
              ),
              data: data.dataset_source_description_secondary,
            },
            {
              title: (
                <React.Fragment>
                  <span>{`Source Description`}</span>
                  <span className="second-row">{`${data.connector_name}`}</span>
                </React.Fragment>
              ),
              data: data.dataset_source_description,
            },
            { title: 'Source Id', data: data.dataset_source_id },
            { title: 'Source URL', data: data.dataset_source_url, isUrl: true },
            {
              title: 'Source Last Altered',
              data: formatData(data.dataset_source_modified, 'datetime'),
            },
            { title: 'Source Created', data: formatData(data.dataset_source_created, 'datetime') },
            ...renderCustomPropertyValues().details,
            { title: 'Id', data: data.dataset_id, tooltip: 'Katalogue-internal id for this asset' },
          ]}
          openLink={sourceLinkAction(
            data.dataset_source_url,
            data.connector_code,
            data.connector_name
          )}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editDataset', [data], editDataset),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteDataset', [data], deleteDataset),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={true}
        >
          {canHaveChildren && (
            <div
              label="Datasets"
              tabid="datasets"
              resultCount={
                loadingChildren || childDatasets.length === 0 ? undefined : childDatasets.length
              }
            >
              <DataTable
                columns={[
                  {
                    id: 'dataset_name',
                    name: 'Name',
                    link: ':link',
                    tooltip: 'dataset_description',
                    tooltipSubtitle: 'dataset_path',
                    // Per row: a dataset's name is the heading of what sits under it, a field's is not
                    className: ':className',
                    inline: [
                      // For API datasources: top-level endpoints show the method as a pill, while
                      // child datasets (request body / response / parameters) show their type icon.
                      ...(data.datasource_type_code === 'api'
                        ? [
                            {
                              type: 'pill',
                              value: 'dataset_subtype_operation_top',
                              label: 'dataset_subtype_name',
                              values: DatasetSubtypeOperation,
                              tooltip: 'dataset_subtype_tooltip',
                              className: 'width-50',
                            },
                            {
                              type: 'icon',
                              icon: 'dataset_type_child',
                              tooltip: 'dataset_type_name',
                              // Mutually exclusive with the method pill per row — share the pill's
                              // single header spacer/sort control instead of adding a second one.
                              sortable: false,
                            },
                          ]
                        : // Everywhere else the dataset's type icon rides in the name cell rather
                          // than in a column of its own, so the expand control sits next to the
                          // checkbox instead of behind an icon.
                          [
                            {
                              type: 'icon',
                              icon: 'dataset_type_icon',
                              tooltip: 'dataset_type_name',
                            },
                          ]),
                      // What a field is, for the field rows nested under a dataset — the same things
                      // its own list shows beside its name, and in the same order: what kind of
                      // thing it is before the name, what it holds after it. None of them belongs to
                      // a dataset, so they claim no header slot.
                      { type: 'icon', icon: 'key_type', tooltip: 'key_list', sortable: false },
                      {
                        type: 'icon',
                        icon: 'datatype_category',
                        tooltip: 'datatype_fullname',
                        sortable: false,
                      },
                      {
                        /*
                        type: 'text',
                        value: 'datatype_fullname',
                        tooltip: 'datatype_fullname',
                        sortable: false,
                        after: true,
                      */
                      },
                      {
                        type: 'pill',
                        value: 'is_nullable',
                        label: 'not_null',
                        values: IsNullableValues,
                        sortable: false,
                        after: true,
                      },
                    ],
                  },
                  {
                    id: 'dataset_description',
                    name: 'Description',
                    type: 'description',
                    viewMode: 'descriptions',
                  },
                  {
                    id: 'field_count',
                    name: 'Fields',
                    type: 'chart',
                    // A field has no fields of its own, so the cell is left blank rather than
                    // reading "-", which would say the number is unknown.
                    appliesTo: Asset.Dataset,
                    chartType: 'bar',
                    data: { data: 'field_count', max: 'field_count_max' },
                    viewMode: 'statistics',
                  },
                  ...(data.datasource_type_code !== 'api'
                    ? [
                        {
                          id: 'row_count',
                          name: 'Rows',
                          type: 'chart',
                          appliesTo: Asset.Dataset,
                          chartType: 'bar',
                          data: { data: 'row_count', max: 'row_count_max' },
                          viewMode: 'statistics',
                        },
                      ]
                    : []),
                  {
                    id: 'attribute_ratio',
                    appliesTo: Asset.Dataset,
                    name: 'Documentation Level',
                    type: 'chart',
                    chartType: 'percent-bar',
                    data: { data: 'attribute_ratio' },
                    viewMode: 'statistics',
                  },
                  {
                    id: 'pii',
                    name: '',
                    type: 'icon',
                    tooltip: 'pii_tooltip',
                    className: 'no-right-margin',
                  },
                  {
                    id: 'attribute_sensitivity_name',
                    name: 'Data Sensitivity',
                    type: 'pill',
                    value: 'attribute_sensitivity_level',
                    values: AttributeSensitivity,
                    tooltip: 'attribute_sensitivity_name',
                  },
                  ...(settings.SHOW_SYSTEM_STATUSES
                    ? [
                        {
                          id: 'status_name',
                          name: 'Status',
                          type: 'pill',
                          value: 'status_id',
                          values: statuses,
                          tooltip: 'status_description',
                        },
                      ]
                    : []),
                  {
                    id: 'principal_name',
                    name: 'Owner',
                    type: 'owner',
                    // An owner belongs to the asset itself: a field inherits its parent's, and showing it
                    // again on every row would read as the field having an owner of its own.
                    appliesTo: Asset.Dataset,
                    link: '/:principal_url',
                    data: { photo: 'principal_photo' },
                    tooltipSubtitle: 'principal_type_name',
                  },
                ]}
                buttons={[
                  {
                    label: '+ Add',
                    action: 'add',
                    mainButton: true,
                    onClick: items => handleChildAsset('add', items),
                  },
                  getChildAddChildButton(),
                  {
                    label: 'Edit',
                    action: 'edit',
                    disabled: !enableChildEdit,
                    tooltip: !enableChildEdit && mixedChildSelectionText,
                    onClick: items => handleChildAsset('edit', items),
                  },
                  {
                    label: 'Delete',
                    action: 'delete',
                    disabled: !enableChildEdit,
                    tooltip: !enableChildEdit && mixedChildSelectionText,
                    onClick: items => handleChildAsset('delete', items),
                  },
                ]}
                data={childAssetRows}
                idColumn="unique_id"
                primaryColumn="dataset_name"
                parentIdColumn="parent_unique_id"
                expandable={true}
                selectedItems={selectedChildItems}
                setSelectedItems={setSelectedChildItems}
                loading={loadingChildren}
                editable={editable}
                filterable={true}
                filterObjects={[
                  'dataset_name',
                  'dataset_description',
                  'dataset_type_name',
                  'is_pii',
                  'attribute_sensitivity_name',
                  ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
                  'principal_name',
                  'connector_filter_name',
                  // A field's datatype. Only fields have one, so it is ticked under its own name
                  // rather than sharing a column with the datasets' type.
                  'datatype_name',
                ]}
                showViewModes={true}
                // A dataset's fields are read when it is opened, so the count of the fields it holds
                // directly is what tells the table it has any before they exist.
                childCountColumn="own_field_count"
                onExpand={fetchChildDatasetFields}
                childFilter={{ fetch: searchChildDatasetFields }}
              />
            </div>
          )}

          {!canHaveChildren && (
            <div
              label="Fields"
              tabid="fields"
              resultCount={loadingTable || fields.length === 0 ? undefined : fields.length}
            >
              <DataTable
                columns={[
                  {
                    id: 'field_name',
                    name: 'Name',
                    link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
                    tooltip: 'field_description',
                    tooltipSubtitle: 'field_path',
                    className: 'bold',
                    inline: [
                      { type: 'icon', icon: 'key_type', tooltip: 'key_list' },
                      { type: 'icon', icon: 'datatype_category', tooltip: 'datatype_fullname' },
                    ],
                  },

                  {
                    id: 'datatype_fullname',
                    name: 'Datatype',
                    className: 'small-text',
                    viewMode: 'statistics',
                  },
                  {
                    id: 'is_nullable',
                    name: '',
                    type: 'boolean',
                    values: IsNullableValues,
                  },
                  {
                    id: 'field_description',
                    name: 'Description',
                    type: 'description',
                    viewMode: 'descriptions',
                  },
                  {
                    id: 'pii',
                    name: '',
                    type: 'icon',
                    tooltip: 'pii_tooltip',
                    className: 'no-right-margin',
                  },
                  {
                    id: 'attribute_sensitivity_name',
                    name: 'Data Sensitivity',
                    type: 'pill',
                    value: 'attribute_sensitivity_level',
                    values: AttributeSensitivity,
                    tooltip: 'attribute_sensitivity_name',
                  },
                  {
                    id: 'attribute_role_name',
                    name: '',
                    type: 'icon',
                    tooltip: 'attribute_role_name',
                    className: 'no-right-margin',
                    viewMode: 'statistics',
                  },
                  {
                    id: 'attribute_name',
                    name: 'Associated Attribute',
                    link: '/attributes/:attribute_id',
                    tooltip: 'field_description',
                    viewMode: 'statistics',
                  },
                  ...(settings.SHOW_SYSTEM_STATUSES
                    ? [
                        {
                          id: 'status_name',
                          name: 'Status',
                          type: 'pill',
                          value: 'status_id',
                          values: statuses,
                          tooltip: 'status_description',
                        },
                      ]
                    : []),
                ]}
                buttons={[
                  {
                    label: '+ Add',
                    action: 'add',
                    mainButton: true,
                    onClick: items => showSideDialog('addField', items || selectedItems, addField),
                  },
                  getAddChildButton(),
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items =>
                      showSideDialog('editField', items || selectedItems, editField),
                  },
                  {
                    label: 'Delete',
                    action: 'delete',
                    onClick: items =>
                      showModalDialog('deleteField', items || selectedItems, deleteField),
                  },
                ]}
                data={fields}
                idColumn="field_id"
                parentIdColumn="parent_field_id"
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                editable={editable}
                loading={loadingTable}
                expandable={true}
                filterable={true}
                filterObjects={[
                  'key_type',
                  'field_name',
                  'datatype_name',
                  'is_nullable',
                  'is_pii',
                  'attribute_sensitivity_name',
                  'attribute_role_name',
                  'field_description',
                  ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
                  'principal_name',
                  'connector_filter_name',
                ]}
                showViewModes={true}
              />
            </div>
          )}

          <div label="Relationships" tabid="relationships">
            <RelationshipGraph
              datasetId={dataset_id}
              objectName={Asset.Dataset}
              objectId={dataset_id}
            />
          </div>

          <div label="Lineage" tabid="lineage">
            <LineageGraph
              datasetId={dataset_id}
              objectName={Asset.Dataset}
              objectId={dataset_id}
              graphType="dataset_lineage"
            />
          </div>

          <div
            label="Related Assets"
            tabid="related_assets"
            resultCount={toReferencesCount && toReferencesCount.total}
          >
            <References
              showMessage={showMessage}
              toObjectName={Asset.Dataset}
              toObjectId={dataset_id}
              toReferencesCount={toReferencesCount}
            />
          </div>

          <div
            label="Technical Definition"
            tabid="technical_definition"
            disabled={!(data.dataset_technical_definition || data.dataset_technical_definition_raw)}
          >
            <TechnicalDefinition
              data={data}
              showInlineToolbar={true}
              language={technicalDefinitionLanguage(
                data.datasource_type_code,
                Asset.Dataset,
                data.dataset_type_code
              )}
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Dataset}
              objectId={dataset_id}
              filterObjects={[
                'changedData.new_data||old_data.dataset_name',
                'changedData.new_data||old_data.dataset_description',
                'changedData.new_data||old_data.dataset_group_name',
                'changedData.new_data||old_data.dataset_type_name',
                'changedData.new_data||old_data.principal_name',
                'fields.field_name',
                'fields.operation',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
                operation: Operation,
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
